import React, { useState, useMemo, useEffect } from 'react';
import { Users, Search, Shield, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
  Button, StatusBadge, ICON_PX,
} from '../ui';
import { usePracticeManagement } from '../../contexts/PracticeManagementContext';

function DelegateModal({ open, onOpenChange, task, onDelegate }) {
  const { staffRoster } = usePracticeManagement();
  const [search, setSearch] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedStaff(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return staffRoster;
    const q = search.toLowerCase();
    return staffRoster.filter((s) =>
      s.name.toLowerCase().includes(q) || s.role.toLowerCase().includes(q)
    );
  }, [staffRoster, search]);

  if (!task) return null;

  const handleSubmit = () => {
    if (!selectedStaff) return;
    onDelegate?.(task.id, { name: selectedStaff.name, id: selectedStaff.id });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Task</DialogTitle>
          <DialogDescription>{task.taskName || task.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-6 py-3">
          {/* Search */}
          <div className="relative">
            <Search size={ICON_PX.md} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff..."
              className="w-full rounded-md border border-nn-border bg-nn-white pl-8 pr-3 py-1.5 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/40"
            />
          </div>

          {/* Staff list */}
          <div className="max-h-60 overflow-y-auto space-y-1 border border-nn-border rounded-lg p-1.5">
            {filtered.map((staff) => {
              const isSelected = selectedStaff?.id === staff.id;
              return (
                <button
                  key={staff.id}
                  type="button"
                  disabled={!staff.available}
                  onClick={() => setSelectedStaff(staff)}
                  className={[
                    'w-full flex items-center gap-2.5 p-2 rounded-md text-left transition-colors',
                    isSelected ? 'bg-aqua-glow/30 border border-aqua' : 'hover:bg-nn-bg-subtle border border-transparent',
                    !staff.available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                  ].join(' ')}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-aqua bg-aqua text-white' : 'border-gray-300'}`}>
                    {isSelected && <Check size={12} strokeWidth={3} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-heading-sm text-text-dark">{staff.name}</span>
                      {!staff.available && <StatusBadge variant="watch">Unavailable</StatusBadge>}
                    </div>
                    <div className="flex items-center gap-2 text-body-sm text-text-muted">
                      <span className="capitalize">{staff.role.replace(/_/g, ' ')}</span>
                      {staff.credentials.length > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Shield size={ICON_PX.xs} />
                          {staff.credentials.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected confirmation */}
          {selectedStaff && (
            <div className="text-body-sm text-text-secondary p-2 bg-nn-bg-subtle rounded-lg">
              Assigning to <span className="font-medium">{selectedStaff.name}</span> ({selectedStaff.role.replace(/_/g, ' ')}).
              An email notification will be sent with task details and a completion link.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="default" size="sm" disabled={!selectedStaff} onClick={handleSubmit}>
            <Users size={ICON_PX.md} className="mr-1" /> Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DelegateModal;
